import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, BookOpen, FileText, Inbox, HardDriveDownload, ClipboardCheck, Plus } from "lucide-react";
import { api } from "../../lib/api";
import { fmtDate } from "../../components/PostCard";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return <div className="text-slate-500">Loading…</div>;

  const tiles = [
    { label: "Blog posts", value: stats.posts, sub: `${stats.drafts} drafts`, icon: FileText, to: "/admin/posts" },
    { label: "Publications", value: stats.publications, sub: `${stats.pdfs_migrated} PDFs in storage`, icon: BookOpen, to: "/admin/publications" },
    { label: "Triage pending", value: stats.triage_pending, sub: `${stats.flagged} flagged for review`, icon: ClipboardCheck, to: "/admin/triage", warn: stats.flagged > 0 },
    { label: "New leads", value: stats.leads_new, sub: "awaiting response", icon: Inbox, to: "/admin/leads" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><div className="eyebrow">Overview</div><h1 className="mt-1 text-2xl font-bold text-[var(--navy)] sm:text-3xl">Dashboard</h1></div>
        <Link to="/admin/posts/new" data-testid="dashboard-new-post" className="btn-primary !py-2.5"><Plus size={15} /> New post</Link>
      </div>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((t) => (
          <Link key={t.label} to={t.to} data-testid={`stat-${t.label.toLowerCase().replace(/\s+/g, "-")}`} className="card">
            <div className="flex items-center justify-between"><t.icon size={20} className="text-[var(--teal)]" />{t.warn && <AlertTriangle size={16} className="text-amber-500" />}</div>
            <div className="mt-4 font-display text-3xl font-bold text-[var(--navy)]">{t.value}</div>
            <div className="text-sm font-medium text-slate-700">{t.label}</div>
            <div className="mt-1 text-xs text-slate-500">{t.sub}</div>
          </Link>
        ))}
      </div>
      <div className="mt-6 grid gap-4 lg:grid-cols-12">
        <div className="card lg:col-span-7">
          <div className="flex items-center justify-between"><h2 className="text-lg font-semibold text-[var(--navy)]">Recent leads</h2><Link to="/admin/leads" className="text-sm font-semibold text-[var(--teal)]">Open inbox</Link></div>
          <div className="mt-4 divide-y divide-slate-100">
            {stats.recent_leads.length === 0 && <div className="py-6 text-sm text-slate-500">No leads yet.</div>}
            {stats.recent_leads.map((l) => (
              <div key={l.id} className="flex items-start justify-between gap-4 py-3 text-sm">
                <div><div className="font-semibold text-slate-800">{l.name} <span className="font-normal text-slate-500">· {l.company || l.email}</span></div><div className="mt-0.5 line-clamp-1 text-slate-600">{l.message}</div></div>
                <div className="shrink-0 font-mono text-xs text-slate-400">{fmtDate(l.created_at)}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="card lg:col-span-5">
          <div className="flex items-center gap-2"><HardDriveDownload size={18} className="text-[var(--teal)]" /><h2 className="text-lg font-semibold text-[var(--navy)]">WordPress migration</h2></div>
          {stats.last_migration ? (
            <div className="mt-4 space-y-2 text-sm text-slate-700">
              <div>Status: <span className={`font-mono text-xs uppercase ${stats.last_migration.status === "completed" ? "text-emerald-600" : stats.last_migration.status === "running" ? "text-amber-600" : "text-rose-600"}`}>{stats.last_migration.status}</span></div>
              <div>Posts imported: <b>{stats.last_migration.posts_imported}</b></div>
              <div>Publications imported: <b>{stats.last_migration.publications_imported}</b></div>
              <div>PDFs migrated to storage: <b>{stats.last_migration.pdfs_migrated}</b></div>
              <div className="text-xs text-slate-500">Last run {fmtDate(stats.last_migration.started_at)}</div>
            </div>
          ) : <p className="mt-4 text-sm text-slate-500">No migration has run yet.</p>}
          <Link to="/admin/triage" className="btn-outline mt-5 !py-2 !text-xs">Open triage workspace</Link>
        </div>
      </div>
    </div>
  );
}
